import { Control, Controller, FieldErrors, UseFormClearErrors, UseFormSetValue } from "react-hook-form";
import React, { useContext, useState } from "react";

import { FileUploader } from "react-drag-drop-files";
import { IFieldFile } from "../InputFields.types";
import { AppContext } from "../../../../util/context";
import block from 'bem-css-modules';
import { exceedsUploadLimit, sanitizeContent as s, trackEvent } from "../../../../util/functions";
import styles from "./FileInput.module.scss";
import { uploadFileIcon, fileUploadedIcon } from "./icons";

const totalSizeMessages: { [locale: string]: string } = {
  "de-DE": "Die Dateien sind zusammen zu groß. Bitte laden Sie insgesamt höchstens 19 MB hoch.",
  "fr-FR": "Les fichiers sont trop volumineux ensemble. Veuillez télécharger 19 Mo au maximum au total.",
};

const FileInput = (props: { fieldData: IFieldFile, setValue: UseFormSetValue<any>, errors: FieldErrors, clearErrors: UseFormClearErrors<any>, control: Control }) => {
  const { 
    fieldData: { 
      label,
      title,
      text,
      placeholder,
      uploadAreaDescription,
      uploadSuccessful,
      uploadSuccessfulDescription,
      multipleFiles,
      validationTextFileSize,
      validationTextMissingFile,
      validationTextFilesLength,
      settings: [{
        crmProperty,
        required
      }]
    },
    setValue,
    errors,
    clearErrors,
    control
  } = props

  const b = block(styles);
  // <FileUploader/> doesn't add the type or size error to the react-form-hook errors object.
  // So we are using a workaround to track the file type/size error.
  const [uploadedFiles, setUploadedFiles] = useState<Array<File>>([]);
  const [fileError, setFileError] = useState(false);
  const [validationText, setValidationText] = useState(validationTextMissingFile);
  const { settings: { locale } } = useContext(AppContext);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [fileName, setFileName] = useState("");

  const fileTypes = ["JPG", "JPEG", "PNG", "GIF", "PDF", "WEBP", "HEIC"];

  const handleChange = async (file: any) => {
    clearErrors(label);
    setFileError(false);
    setValue(crmProperty, file);
    trackEvent("formClient.field.file.selected", label);

    setFileLoading(true);
    setTimeout(() => {
      setFileLoading(false);
      setFileUploaded(true);
      setFileName(file.name);
    }, 4000);
  };

  const handleTypeError = () => {
    setFileError(true);
    setValidationText(validationTextMissingFile);
    trackEvent("formClient.field.file.typeError", label);
  }

  const handleSizeError = () => {
    setFileError(true);
    setValidationText(validationTextFileSize);
    trackEvent("formClient.field.file.sizeError", label);
  }

  // Each file can be under the limit while all of them together are not, so the per-file
  // Dato copy ("Die Datei ist zu groß") would be wrong here.
  const handleTotalSizeError = () => {
    setFileError(true);
    setValidationText(totalSizeMessages[locale] ?? totalSizeMessages["de-DE"]);
    trackEvent("formClient.field.file.totalSizeError", label);
  }

  const handleFilesLengthError = (selectedFiles: File | File[] | FileList) => {
    const filesArray: File[] = selectedFiles instanceof File
      ? [selectedFiles]
      : Array.from(selectedFiles as ArrayLike<File>);

    if(multipleFiles) {
      const allFiles: Array<File> = Array.from(uploadedFiles).concat(filesArray);
      // The upload is one request, and the ingress rejects bodies over 20 MB
      // with a 413 the browser cannot read (no CORS headers on it). Refuse the
      // selection here instead, while the reader can still pick smaller files.
      if (exceedsUploadLimit(allFiles)) {
        handleTotalSizeError();
        return;
      }
      setUploadedFiles(allFiles);
  
      const fullFileList = new DataTransfer();
      allFiles.forEach(file => fullFileList.items.add(file));
  
      if(fullFileList.files.length > 10) { 
        setFileError(true);
        setValidationText(validationTextFilesLength);
        return;
      } else {
        handleChange(fullFileList.files);
      }
    } else {
      handleChange(selectedFiles);
    }
  }

  return (
    <div className={`${b()}`}>
      {title && (
        <h3 className={b("title")} dangerouslySetInnerHTML={{ __html: s(title) }}/>
      )}
      {text && <div className={b("text")} dangerouslySetInnerHTML={{ __html: s(text) }} />}
      <div className={b("upload-area")}>
        <Controller
          name={crmProperty}
          control={control}
          rules={{ required: required }}
          render={() => (
            <FileUploader 
              multiple={multipleFiles}
              types={fileTypes} 
              maxSize={19}
              onTypeError={handleTypeError}
              onSizeError={handleSizeError}
              label={placeholder}
              handleChange={handleFilesLengthError}
            />
          )}
        />

        <div className={`${b("custom-uploader")} ${fileUploaded ? styles['file-uploaded'] : ''} ${fileLoading ? styles['file-loading'] : ''}`}>
          {!fileUploaded && (
            <>
              <div className={b("header")}>
                <div className={b("icon")}>
                  {uploadFileIcon}
                </div>
                <div className={b("uploader-title")} dangerouslySetInnerHTML={{ __html: s(placeholder) }}/>
              </div>
              <div className={b("uploader-description")} dangerouslySetInnerHTML={{ __html: s(uploadAreaDescription) }}/>
            </>
          )}

          {fileLoading && (
            <div className={b("file-loading")}>
              <span/>
            </div>
          )}

          {fileUploaded && (
            <>
              <div className={b("header")}>
                <div className={b("uploaded-icon")}>
                  {fileUploadedIcon}
                </div>
                <div className={b("title")} dangerouslySetInnerHTML={{ __html: s(uploadSuccessful) }}/>
              </div>
              <div className={b("description")} dangerouslySetInnerHTML={{ __html: s(uploadSuccessfulDescription) }}/>
              {!multipleFiles && (
                <div className={b("file-name")}>
                <span>{fileName}</span>
              </div>
              )}
            </>
          )}
        </div>
      </div>


      {/* required check & file (type + size) check */}
      {(errors[crmProperty] || fileError) &&  (
        <div className={b("error")} dangerouslySetInnerHTML={{ __html: s(validationText) }} />
      )}
      {(uploadedFiles.length > 0 && multipleFiles) && <div className={b("center")}> {uploadedFiles.length} 📂 </div>}
    </div>
  )
}

export default FileInput;